<template>
	<div class="checkout-additional-info">
		<CartCheckoutBanner
			v-if="showCheckoutBanner"
			:title="bannerTitle"
			:body="bannerBody"
		/>
		<AlcoholDisclaimer v-else-if="hasAlcoholInCart" />
		<div class="additional-fees-wrapper">
			<button
				class="button button-transparent button-coupon"
				data-cy="coupon-button"
				@click="couponClicked"
			>
				<slot v-if="couponCode">
					<span>
						<i class="sk-icon-times"></i>
						{{ couponCode }} Applied!
					</span>
					<span class="no-fee">-{{ discount | currency }}</span>
				</slot>
				<slot v-if="!couponCode">
					<span v-if="showVoucherCta">
						<i class="sk-icon-plus"></i>
						Promo Code?
					</span>
					<span v-else>
						<i class="sk-icon-plus"></i>
						Add a coupon?
					</span>
				</slot>
			</button>
		</div>
		<div class="additional-fee">
			Subtotal
			<span class="additional-fee-price">
				{{ subtotal | currency }}
			</span>
		</div>
		<div class="additional-fee">
			Tax & Fees
			<span class="additional-fee-price">
				{{ totalTaxFees | currency }}
			</span>
		</div>
		<div class="additional-fee" v-if="showDeliveryFee">
			{{ deliveryFeeText }}
			<span class="no-fee" v-if="noDeliveryFee">FREE</span>
			<span class="additional-fee-price" v-else>
				{{ deliveryFee | currency }}
			</span>
		</div>
		<div v-if="showTipOptions" class="additional-fee">
			Tip
			<span class="additional-fee-price" data-cy="tip-amount">
				{{ totalTip | currency }}
			</span>
		</div>
		<SkButtonGroup
			v-if="showTipOptions"
			class="tip-options"
			:buttons="tipOptions"
			@buttonSelected="tipOptionSelected"
		/>
		<div class="total-price">
			<span>Total</span>
			<span>{{ totalPrice | currency }}</span>
		</div>
		<TipModal />
	</div>
</template>

<style scoped>
.checkout-additional-info {
	padding: 0 20px;
}

.alcohol-disclaimer {
	margin-top: 25px;
	margin-bottom: 30px;
}

.button-coupon {
	color: var(--gR-base-90);
	width: 100%;
	height: 18px;
	justify-content: space-between;
	padding: 0;
	font-size: 12px;
	margin-top: 18px;
}

.button-coupon span {
	display: flex;
	align-items: center;
}

.button-coupon i {
	margin-right: 10px;
	font-size: 15px;
	color: var(--gR-base-90);
}

.additional-fee {
	display: flex;
	justify-content: space-between;
	font-size: 12px;
	color: var(--sk-grey);
	margin-top: 5px;
}

.additional-fee .additional-fee-price {
	color: var(--sk-grey);
}

.no-fee {
	color: var(--gR-teal-70);
	font-weight: 500;
}

.tip-options {
	max-width: 500px;
	margin: 12px auto 25px auto;
}

.total-price {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-top: 5px;
}

.total-price span {
	font-weight: 600;
	font-size: 14px;
	color: var(--gR-base-80);
}
</style>

<script>
import { ResidentialBannerType } from "@/enums/ResidentialBannerType"
import { eventBus } from "@/eventBus"
import { EventBusEvents } from "@/enums/EventBusEvents"
import residentialBannerMixin from "@/mixins/residential-banner-mixin"
import currencyFilterMixin from "@/mixins/currency-filter-mixin"
import SkButtonGroup from "@/components/common/SkButtonGroup.vue"
import TipModal from "@/components/modals/TipModal.vue"
import AlcoholDisclaimer from "@/components/common/AlcoholDisclaimer.vue"
import CartCheckoutBanner from "@/components/checkout/CartCheckoutBanner.vue"
import { DeliveryMode } from "@reef-os/utils/enums/deliveryMode"

export default {
	name: "CheckoutAdditionalInfo",
	mixins: [currencyFilterMixin, residentialBannerMixin],
	components: {
		SkButtonGroup,
		TipModal,
		AlcoholDisclaimer,
		CartCheckoutBanner
	},
	computed: {
		showVoucherCta() {
			return this.$store.state.showVouchers
		},
		showCheckoutBanner() {
			return (
				this.bannerType == ResidentialBannerType.FIRST_ORDER ||
				this.bannerType == ResidentialBannerType.LOYALTY ||
				this.bannerType == ResidentialBannerType.LOYALTY_COMPLETE
			)
		},
		bannerTitle() {
			switch (this.bannerType) {
				case ResidentialBannerType.FIRST_ORDER: {
					let discount =
						this.discount > 0
							? this.discount
							: this.firstTimeCoupon.amount
					return `YOU’RE GETTING $${discount} OFF!`
				}
				case ResidentialBannerType.LOYALTY:
					return "ALMOST THERE!"
				case ResidentialBannerType.LOYALTY_COMPLETE:
					return "REWARD APPLIED!"
			}
			return null
		},
		bannerBody() {
			switch (this.bannerType) {
				case ResidentialBannerType.FIRST_ORDER: {
					let discount =
						this.discount > 0
							? this.discount
							: this.firstTimeCoupon.amount
					return `Thanks for giving 2ndKitchen a try. Enjoy $${discount} off your first order to celebrate!`
				}
				case ResidentialBannerType.LOYALTY:
					return "You’re only one order away from your next reward! It will be automatically applied on your next order."
				case ResidentialBannerType.LOYALTY_COMPLETE: {
					let discount =
						this.discount > 0
							? this.discount
							: this.loyaltyCampaign.campaign.parameters
									.coupon_amount
					return `You made it to ${this.loyaltyCampaign.progress.goal} orders! Enjoy $${discount} off this order as a loyal 2ndKitchen customer.`
				}
			}
			return null
		},
		showDeliveryFee() {
			return this.$store.getters.showCartDeliveryFee
		},
		noDeliveryFee() {
			return this.deliveryFee <= 0
		},
		deliveryFeeText() {
			if (
				this.$store.getters.isAirport &&
				this.$store.state.deliveryMode ==
					DeliveryMode.DIRECT_TO_LOCATION
			) {
				return "Gate Delivery"
			}
			return "Neighborhood Delivery"
		},
		deliveryFee() {
			return this.$store.getters.deliveryFee
		},
		showTipOptions() {
			if (
				this.$store.getters.isAirport &&
				(this.$store.state.deliveryMode == null ||
					this.$store.state.deliveryMode == DeliveryMode.LOCKER || this.$store.state.deliveryMode == DeliveryMode.DIRECT_TO_LOCATION)
			) {
				return false
			}
			return true
		},
		tipOptions() {
			if (!this.$store.state.business.tip_options) {
				return []
			}
			const selectedTipValue = this.$store.state.tipPer
			let tipOptionFound = false
			const tipOptions = this.$store.state.business.tip_options.map(
				(option) => {
					let name =
						option.value !== -1 ? option.value + "%" : "Custom"
					let is_selected = selectedTipValue
						? option.value == selectedTipValue
						: option.is_default
					tipOptionFound = tipOptionFound
						? tipOptionFound
						: is_selected
					return {
						name: name,
						is_default: is_selected,
						value: option.value,
						is_selected: is_selected
					}
				}
			)
			if (!tipOptionFound) {
				let customOption = tipOptions.find(
					(option) => option.value == -1
				)
				customOption.is_selected = true
			}
			return tipOptions
		},
		totalTip() {
			return this.$store.getters.totalTip
		},
		totalTaxFees() {
			return this.$store.getters.totalTaxFees
		},
		discount() {
			return this.$store.state.discount
		},
		couponCode() {
			return this.$store.state.coupon.code.toUpperCase()
		},
		hasAlcoholInCart() {
			return this.$store.state.cart.some(
				(product) => product.contains_alcohol == 1
			)
				? 1
				: 0
		},
		subtotal() {
			return this.$store.getters.price
		},
		totalPrice() {
			return this.$store.getters.totalCheckoutPrice
		}
	},
	methods: {
		setTipPer(tipOption) {
			this.$store.dispatch("setCurrentTipCustom", 0)
			this.$store.dispatch("setCurrentTipPer", tipOption.value)
		},
		tipOptionSelected(tipOption) {
			if (tipOption.value > 0) {
				this.setTipPer(tipOption)
			} else {
				$("#tipmodal").modal("show")
			}
		},
		couponClicked() {
			if (this.$store.state.coupon.code) {
				this.$store.dispatch("sendMixpanelEvent", {
					msg: "Remove Coupon Clicked",
					data: {
						code: this.$store.state.coupon.code
					}
				})
				this.$store.dispatch("removeCoupon")
			} else {
				this.$store.dispatch("sendMixpanelEvent", {
					msg: "Add Coupon Clicked"
				})
				eventBus.emit(EventBusEvents.OPEN_COUPON_MODAL)
			}
		}
	}
}
</script>
